import React from "react";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { buildDefaultDesignTokens } from "../../landingEditor/config";
import GoogleFontPicker from "../../landingEditor/GoogleFontPicker";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const DESIGN_TOKEN_COLOR_PATTERN = /^(?:#[0-9a-f]{3,8}|rgba?\(\s*[0-9.]+%?\s*,\s*[0-9.]+%?\s*,\s*[0-9.]+%?(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)|hsla?\(\s*[0-9.]+(?:deg)?\s*,\s*[0-9.]+%\s*,\s*[0-9.]+%(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)|transparent|currentcolor)$/i;
const INVALID_COLOR_HELPER = "Usá un color válido, por ejemplo #F35A3A, rgba(...) o transparent.";

const TOKEN_GROUPS = [
  { id: "colors", label: "Colores", description: "Paleta global usada cuando un segmento no tiene un color propio." },
  { id: "typography", label: "Tipografía", description: "Familias, tamaños, pesos e interlineados generales." },
  { id: "spacing", label: "Espaciado", description: "Escala común de separaciones y aire entre contenidos." },
  { id: "borders", label: "Bordes", description: "Grosor, estilo, radios y color base de los contenedores." },
  { id: "shadows", label: "Sombras", description: "Profundidad de tarjetas, superficies y foco visual." },
];

const TOKEN_FIELDS = {
  colors: [
    ["primary", "Principal", "color"],
    ["secondary", "Secundario", "color"],
    ["background", "Fondo general", "color"],
    ["surface", "Superficie", "color"],
    ["text", "Texto principal", "color"],
    ["mutedText", "Texto secundario", "color"],
    ["divider", "Divisores", "color"],
    ["onPrimary", "Texto sobre principal", "color"],
    ["onSecondary", "Texto sobre secundario", "color"],
    ["success", "Éxito", "color"],
    ["warning", "Advertencia", "color"],
    ["error", "Error", "color"],
  ],
  typography: [
    ["baseFontSize", "Tamaño base", "number", { min: 10, max: 24, step: 1, suffix: "px" }],
    ["scaleRatio", "Escala de títulos", "number", { min: 1, max: 2, step: 0.05 }],
    ["lineHeight", "Interlineado", "number", { min: 1, max: 2.5, step: 0.05 }],
    ["headingLineHeight", "Interlineado de títulos", "number", { min: 0.8, max: 2, step: 0.05 }],
    ["fontWeightRegular", "Peso regular", "number", { min: 100, max: 1000, step: 100 }],
    ["fontWeightMedium", "Peso medio", "number", { min: 100, max: 1000, step: 100 }],
    ["fontWeightBold", "Peso fuerte", "number", { min: 100, max: 1000, step: 100 }],
    ["letterSpacing", "Espaciado de letras", "number", { min: -4, max: 12, step: 0.1, suffix: "px" }],
  ],
  spacing: [
    ["unit", "Unidad base", "number", { min: 1, max: 24, step: 1, suffix: "px" }],
    ["xs", "Extra chico", "number", { min: 0, max: 64, step: 1, suffix: "px" }],
    ["sm", "Chico", "number", { min: 0, max: 96, step: 1, suffix: "px" }],
    ["md", "Medio", "number", { min: 0, max: 160, step: 1, suffix: "px" }],
    ["lg", "Grande", "number", { min: 0, max: 240, step: 1, suffix: "px" }],
    ["xl", "Extra grande", "number", { min: 0, max: 320, step: 1, suffix: "px" }],
    ["xxl", "Máximo", "number", { min: 0, max: 480, step: 1, suffix: "px" }],
    ["section", "Separación de secciones", "number", { min: 0, max: 640, step: 1, suffix: "px" }],
  ],
  borders: [
    ["width", "Grosor", "number", { min: 0, max: 12, step: 0.5, suffix: "px" }],
    ["style", "Estilo", "select", { options: ["solid", "dashed", "dotted", "none"] }],
    ["radiusSm", "Radio chico", "number", { min: 0, max: 120, step: 1, suffix: "px" }],
    ["radiusMd", "Radio medio", "number", { min: 0, max: 160, step: 1, suffix: "px" }],
    ["radiusLg", "Radio grande", "number", { min: 0, max: 240, step: 1, suffix: "px" }],
    ["radiusPill", "Radio píldora", "number", { min: 0, max: 9999, step: 1, suffix: "px" }],
    ["color", "Color del borde", "color"],
  ],
  shadows: [
    ["sm", "Sombra chica", "text"],
    ["md", "Sombra media", "text"],
    ["lg", "Sombra grande", "text"],
    ["focus", "Sombra de foco", "text"],
  ],
};

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function mergeDesignTokens(defaults, source) {
  const input = isPlainObject(source) ? source : {};
  return Object.fromEntries(
    TOKEN_GROUPS.map(({ id }) => [
      id,
      {
        ...(defaults[id] || {}),
        ...(isPlainObject(input[id]) ? input[id] : {}),
      },
    ])
  );
}

function finite(value, fallback) {
  if (value === "" || value === null || value === undefined) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function cssPixels(value, fallback = 0) {
  return `${finite(value, fallback)}px`;
}

function normalizeEditableColor(value) {
  const candidate = String(value ?? "").trim();
  if (!candidate) return "";
  const lowered = candidate.toLowerCase();
  if (["transparent", "transparente", "trasparente"].includes(lowered)) return "transparent";
  return candidate;
}

function colorInputValue(value) {
  const candidate = normalizeEditableColor(value);
  if (/^#[0-9a-f]{6}$/i.test(candidate)) return candidate;
  if (/^#[0-9a-f]{3}$/i.test(candidate)) {
    return `#${candidate.slice(1).split("").map((char) => `${char}${char}`).join("")}`;
  }
  return "#000000";
}

function isDesignTokenColor(value) {
  return typeof value === "string" && DESIGN_TOKEN_COLOR_PATTERN.test(value.trim());
}

function TokenField({ field, value, onChange }) {
  const [key, label, type, options = {}] = field;
  const committedColor = type === "color" ? normalizeEditableColor(value) : "";
  const [editableColor, setEditableColor] = React.useState(committedColor);
  const [colorError, setColorError] = React.useState(false);

  React.useEffect(() => {
    if (type !== "color") return;
    setEditableColor(committedColor);
    setColorError(false);
  }, [committedColor, type]);

  const common = {
    fullWidth: true,
    size: "small",
    label,
    value: value ?? "",
  };

  if (type === "select") {
    return (
      <TextField {...common} select onChange={(event) => onChange(key, event.target.value)}>
        {(options.options || []).map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (type === "number") {
    return (
      <TextField
        {...common}
        type="number"
        inputProps={{ min: options.min, max: options.max, step: options.step }}
        InputProps={
          options.suffix
            ? { endAdornment: <InputAdornment position="end">{options.suffix}</InputAdornment> }
            : undefined
        }
        onChange={(event) => {
          const nextValue = event.target.value;
          onChange(key, nextValue === "" ? "" : Number(nextValue));
        }}
      />
    );
  }

  if (type === "color") {
    const transparent = committedColor === "transparent";
    return (
      <TextField
        {...common}
        value={editableColor}
        placeholder="transparent o #RRGGBB"
        error={colorError}
        helperText={
          colorError
            ? INVALID_COLOR_HELPER
            : transparent
              ? "Valor actual: transparent"
              : undefined
        }
        onChange={(event) => {
          const nextText = event.target.value;
          const candidate = normalizeEditableColor(nextText);
          const valid = isDesignTokenColor(candidate);
          setEditableColor(nextText);
          setColorError(!valid);
          if (valid) onChange(key, candidate);
        }}
        onBlur={() => {
          const candidate = normalizeEditableColor(editableColor);
          if (!candidate || !isDesignTokenColor(candidate)) {
            setEditableColor(committedColor);
          } else {
            setEditableColor(candidate);
          }
          setColorError(false);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box
                component="input"
                type="color"
                aria-label={`Elegir ${label.toLowerCase()}`}
                value={colorInputValue(committedColor)}
                onChange={(event) => {
                  setEditableColor(event.target.value);
                  setColorError(false);
                  onChange(key, event.target.value);
                }}
                sx={{ width: 28, height: 28, p: 0, border: 0, bgcolor: "transparent", cursor: "pointer", opacity: transparent ? 0.45 : 1 }}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Button
                size="small"
                onClick={() => {
                  setEditableColor("transparent");
                  setColorError(false);
                  onChange(key, "transparent");
                }}
                sx={{ minWidth: 0, px: 0.7, textTransform: "none", fontSize: ".68rem", fontWeight: 900 }}
              >
                Transparente
              </Button>
            </InputAdornment>
          ),
        }}
      />
    );
  }

  return <TextField {...common} onChange={(event) => onChange(key, event.target.value)} />;
}

function DesignTokenPreview({ tokens }) {
  const colors = tokens.colors || {};
  const typography = tokens.typography || {};
  const spacing = tokens.spacing || {};
  const borders = tokens.borders || {};
  const shadows = tokens.shadows || {};

  return (
    <Box
      sx={{
        p: cssPixels(spacing.lg, 24),
        bgcolor: colors.background,
        color: colors.text,
        borderRadius: cssPixels(borders.radiusLg, 18),
        border: `${finite(borders.width, 1)}px ${borders.style || "solid"} ${borders.color || colors.divider}`,
        boxShadow: shadows.md,
        fontFamily: typography.bodyFontFamily || typography.fontFamily,
      }}
    >
      <Typography
        sx={{
          color: colors.secondary,
          fontFamily: typography.headingFontFamily || typography.fontFamily,
          fontSize: `${finite(typography.baseFontSize, 16) * finite(typography.scaleRatio, 1.2) * 1.35}px`,
          lineHeight: finite(typography.headingLineHeight, 1.15),
          fontWeight: finite(typography.fontWeightBold, 800),
          letterSpacing: cssPixels(typography.letterSpacing, 0),
        }}
      >
        Vista global
      </Typography>
      <Typography
        sx={{
          mt: cssPixels(spacing.sm, 8),
          color: colors.mutedText,
          fontSize: cssPixels(typography.baseFontSize, 16),
          lineHeight: finite(typography.lineHeight, 1.5),
          fontWeight: finite(typography.fontWeightRegular, 400),
        }}
      >
        Estos valores completan el diseño general. Los colores y estilos definidos dentro de un segmento conservan prioridad.
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: cssPixels(spacing.md, 16) }}>
        <Box
          sx={{
            px: cssPixels(spacing.md, 16),
            py: cssPixels(spacing.sm, 8),
            bgcolor: colors.primary,
            color: colors.onPrimary,
            borderRadius: cssPixels(borders.radiusPill, 9999),
            boxShadow: shadows.sm,
            fontWeight: finite(typography.fontWeightMedium, 600),
          }}
        >
          Acción principal
        </Box>
        <Box
          sx={{
            px: cssPixels(spacing.md, 16),
            py: cssPixels(spacing.sm, 8),
            bgcolor: colors.surface,
            color: colors.secondary,
            borderRadius: cssPixels(borders.radiusPill, 9999),
            border: `${finite(borders.width, 1)}px ${borders.style || "solid"} ${borders.color || colors.divider}`,
            fontWeight: finite(typography.fontWeightMedium, 600),
          }}
        >
          Acción secundaria
        </Box>
      </Stack>
    </Box>
  );
}

function DesignTokensDrawer({ open, designTokens, theme, onClose, onChange }) {
  const [activeGroup, setActiveGroup] = React.useState("colors");
  const defaults = React.useMemo(() => buildDefaultDesignTokens(theme), [theme]);
  const tokens = React.useMemo(
    () => mergeDesignTokens(defaults, designTokens),
    [defaults, designTokens]
  );
  const group = TOKEN_GROUPS.find((item) => item.id === activeGroup) || TOKEN_GROUPS[0];

  const updateField = React.useCallback(
    (key, value) => {
      onChange?.({
        ...tokens,
        [activeGroup]: {
          ...tokens[activeGroup],
          [key]: value,
        },
      });
    },
    [activeGroup, onChange, tokens]
  );

  const updateTypographyFonts = React.useCallback(
    (headingFamily, bodyFamily) => {
      const heading = headingFamily || tokens.typography?.headingFontFamily || "Exo 2";
      const body = bodyFamily || tokens.typography?.bodyFontFamily || "Asap";
      onChange?.({
        ...tokens,
        typography: {
          ...tokens.typography,
          fontFamily: body,
          headingFontFamily: heading,
          bodyFontFamily: body,
        },
      });
    },
    [onChange, tokens]
  );

  const resetGroup = React.useCallback(() => {
    onChange?.({
      ...tokens,
      [activeGroup]: { ...defaults[activeGroup] },
    });
  }, [activeGroup, defaults, onChange, tokens]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          maxWidth: "100%",
          bgcolor: "#F7F8FA",
        },
      }}
    >
      <Stack sx={{ minHeight: "100%" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ px: 2, py: 1.6, bgcolor: "#fff" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Tokens globales</Typography>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
              Cambios del borrador con vista previa inmediata
            </Typography>
          </Box>
          <IconButton aria-label="Cerrar tokens globales" onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Tabs
          value={activeGroup}
          onChange={(_event, value) => setActiveGroup(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ bgcolor: "#fff", borderTop: "1px solid rgba(25,36,47,.06)", borderBottom: "1px solid rgba(25,36,47,.08)" }}
        >
          {TOKEN_GROUPS.map((item) => (
            <Tab key={item.id} value={item.id} label={item.label} sx={{ textTransform: "none", fontWeight: 850 }} />
          ))}
        </Tabs>

        <Box sx={{ flex: 1, overflow: "auto", p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={2}>
            <Box>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{group.label}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.3, color: "rgba(25,36,47,.62)" }}>
                    {group.description}
                  </Typography>
                </Box>
                <Tooltip title="Restablecer este grupo">
                  <IconButton
                    size="small"
                    aria-label={`Restablecer ${group?.label || "este grupo"}`}
                    onClick={resetGroup}
                    sx={{ border: "1px solid rgba(25,36,47,.12)" }}
                  >
                    <RestartAltRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {activeGroup === "typography" ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.25,
                  p: 1.5,
                  borderRadius: 3,
                  bgcolor: "#fff",
                  border: "1px solid rgba(25,36,47,.08)",
                }}
              >
                <GoogleFontPicker
                  label="Tipografía de títulos"
                  value={tokens.typography?.headingFontFamily}
                  onChange={(family) => updateTypographyFonts(family, tokens.typography?.bodyFontFamily)}
                />
                <GoogleFontPicker
                  label="Tipografía de textos"
                  value={tokens.typography?.bodyFontFamily}
                  onChange={(family) => updateTypographyFonts(tokens.typography?.headingFontFamily, family)}
                />
              </Box>
            ) : null}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 1.25,
                p: 1.5,
                borderRadius: 3,
                bgcolor: "#fff",
                border: "1px solid rgba(25,36,47,.08)",
              }}
            >
              {(TOKEN_FIELDS[activeGroup] || []).map((field) => (
                <TokenField
                  key={field[0]}
                  field={field}
                  value={tokens[activeGroup]?.[field[0]]}
                  onChange={updateField}
                />
              ))}
            </Box>

            <Divider />
            <Box>
              <Typography variant="overline" sx={{ color: "rgba(25,36,47,.55)", fontWeight: 900 }}>
                Muestra en vivo
              </Typography>
              <DesignTokenPreview tokens={tokens} />
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  );
}

export {
  TOKEN_GROUPS,
  TOKEN_FIELDS,
  isPlainObject,
  mergeDesignTokens,
  finite,
  cssPixels,
  normalizeEditableColor,
  colorInputValue,
  TokenField,
  DesignTokenPreview,
  DesignTokensDrawer
};
